import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Eye, EyeOff, Mail, Lock, User } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { toast } from '@/hooks/use-toast';
import { useSupabaseClient } from '@/hooks/useSupabaseClient';

const loginSchema = z.object({
  email: z.string().email('Ogiltig e-postadress'),
  password: z.string().min(6, 'Lösenordet måste vara minst 6 tecken')
});

const registerSchema = z.object({
  name: z.string().min(2, 'Namn måste vara minst 2 tecken'),
  email: z.string().email('Ogiltig e-postadress'),
  password: z.string().min(6, 'Lösenordet måste vara minst 6 tecken'),
  confirmPassword: z.string().min(6, 'Lösenordet måste vara minst 6 tecken')
}).refine((data) => data.password === data.confirmPassword, {
  message: "Lösenorden matchar inte",
  path: ["confirmPassword"],
});

type AuthFormProps = {
  type: 'login' | 'register';
  onError?: (error: string) => void;
};

const AuthForm = ({ type, onError }: AuthFormProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [resetPassword, setResetPassword] = useState(false);
  const navigate = useNavigate();
  const supabase = useSupabaseClient();

  const schema = type === 'login' ? loginSchema : registerSchema;

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: '',
      password: '',
      ...(type === 'register' && { name: '', confirmPassword: '' }),
    },
  });

  const onSubmit = async (values: z.infer<typeof schema>) => {
    setIsLoading(true);
    
    if (onError) onError(null);

    try {
      if (resetPassword) {
        const { error } = await supabase.auth.resetPasswordForEmail(values.email, {
          redirectTo: `${window.location.origin}/reset-password`,
        });

        if (error) throw error;

        toast({
          title: "Återställning av lösenord",
          description: "Instruktioner har skickats till din e-post.",
        });
        setResetPassword(false);
      } else if (type === 'login') {
        console.log('Attempting login with:', values.email);
        const { error, data } = await supabase.auth.signInWithPassword({
          email: values.email,
          password: values.password,
        });

        if (error) throw error;

        console.log('Login successful, session:', data.session);
        toast({
          title: "Inloggning lyckades",
          description: "Välkommen tillbaka!",
        });
        navigate('/dashboard');
      } else {
        console.log('Attempting registration with:', values.email);
        const { error, data } = await supabase.auth.signUp({
          email: values.email,
          password: values.password,
          options: {
            data: {
              name: (values as any).name,
            },
          },
        });

        if (error) throw error;

        console.log('Registration response:', data);
        
        if (data.user && data.session) {
          toast({
            title: "Registrering lyckades",
            description: "Du är nu inloggad och kommer att omdirigeras till dashboarden.",
          });
          navigate('/dashboard');
        } else {
          toast({
            title: "Registrering lyckades",
            description: "Kontrollera din e-post för att bekräfta ditt konto.",
          });
        }
      }
    } catch (error: any) {
      console.error('Auth error:', error);
      const errorMessage = error.message || "Något gick fel. Försök igen.";
      
      toast({
        title: "Ett fel uppstod",
        description: errorMessage,
        variant: "destructive",
      });
      
      if (onError) onError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => setShowPassword(!showPassword);
  const toggleConfirmPasswordVisibility = () => setShowConfirmPassword(!showConfirmPassword);

  if (resetPassword) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h2 className="text-xl font-semibold">Återställ lösenord</h2>
          <p className="text-sm text-gray-600 mt-2">
            Ange din e-postadress så skickar vi instruktioner för att återställa ditt lösenord.
          </p>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-darkgray">E-post</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                      <Input 
                        {...field} 
                        placeholder="Din e-postadress" 
                        className="pl-10 h-12 border border-gray-300"
                        type="email"
                        disabled={isLoading} 
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex flex-col space-y-2">
              <Button 
                type="submit" 
                disabled={isLoading} 
                className="w-full h-12 bg-gradient-to-r from-purple-500 to-purple-700 hover:from-purple-600 hover:to-purple-800"
              >
                {isLoading ? "Bearbetar..." : "Skicka återställningslänk"}
              </Button>
              <Button 
                type="button" 
                variant="ghost" 
                disabled={isLoading}
                onClick={() => setResetPassword(false)}
                className="w-full"
              >
                Tillbaka till inloggning
              </Button>
            </div>
          </form>
        </Form>
      </div>
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {type === 'register' && (
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-darkgray">Namn</FormLabel>
                <FormControl>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <Input 
                      {...field} 
                      placeholder="Ditt namn" 
                      className="pl-10 h-12 border border-gray-300"
                      disabled={isLoading} 
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-darkgray">E-post</FormLabel>
              <FormControl>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <Input 
                    {...field} 
                    placeholder="Din e-postadress" 
                    className="pl-10 h-12 border border-gray-300"
                    type="email"
                    disabled={isLoading} 
                  />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-darkgray">Lösenord</FormLabel>
              <FormControl>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <Input 
                    {...field} 
                    type={showPassword ? "text" : "password"} 
                    placeholder="Ditt lösenord" 
                    className="pl-10 pr-10 h-12 border border-gray-300"
                    disabled={isLoading} 
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={togglePasswordVisibility}
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                    <span className="sr-only">
                      {showPassword ? "Dölj lösenord" : "Visa lösenord"}
                    </span>
                  </Button>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {type === 'register' && (
          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-darkgray">Bekräfta lösenord</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <Input 
                      {...field} 
                      type={showConfirmPassword ? "text" : "password"} 
                      placeholder="Bekräfta ditt lösenord" 
                      className="pl-10 pr-10 h-12 border border-gray-300"
                      disabled={isLoading} 
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={toggleConfirmPasswordVisibility}
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                      <span className="sr-only">
                        {showConfirmPassword ? "Dölj lösenord" : "Visa lösenord"}
                      </span>
                    </Button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <div className="flex flex-col space-y-4">
          <Button 
            type="submit" 
            disabled={isLoading} 
            className="w-full h-12 bg-gradient-to-r from-yellow-500 to-yellow-300 hover:from-yellow-600 hover:to-yellow-400 text-darkgray"
          >
            {isLoading ? "Bearbetar..." : type === 'login' ? "Logga in" : "Skapa konto"}
          </Button>
          
          {type === 'login' && (
            <Button 
              type="button" 
              variant="link" 
              disabled={isLoading}
              onClick={() => setResetPassword(true)}
              className="text-sm text-gray-500 hover:text-gray-700 p-0 h-auto font-normal"
            >
              Glömt lösenordet?
            </Button>
          )}
        </div>
      </form>
    </Form>
  );
};

export default AuthForm;
